import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import {
  GridRowsProp,
  GridRowModesModel,
  GridRowModes,
  DataGrid,
  GridColDef,
  GridToolbarContainer,
  GridActionsCellItem,
  GridEventListener,
  GridRowId,
  GridRowModel,
  GridRowEditStopReasons,
  GridSlots,
} from '@mui/x-data-grid';
import {
  randomId,
} from '@mui/x-data-grid-generator';
import { AudioUploader, Choice, ImageUploader, Level, MultipleSelect, ShowAudio, showImage } from '../../../Components/Medias';
import { CircularProgress } from '@mui/material';
import Toast from '../../../Common/Helpers/Toast';
import { successToast } from '../../../Common/Helpers/Utils';
import Swal from 'sweetalert2';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../InfraStructure/store';
import LevelsOperations from '../../../InfraStructure/Levels/LevelsOperations';
import { CustomGridToolbarProps, LevelProps, ResponseProps } from '../../../@types/types';

export interface EditToolbarProps {
  setRows: (newRows: (oldRows: GridRowsProp) => GridRowsProp) => void;
  setRowModesModel: (
    newModel: (oldModel: GridRowModesModel) => GridRowModesModel,
  ) => void;
}

type DataProps = {
  data: GridRowsProp;
  onSave?: (data: any) => Promise<ResponseProps>;
  onUpdate?: (data: any) => Promise<ResponseProps>;
  onDelete?: (id: GridRowId) => Promise<boolean>;
}

export default function CrudFlashCards({ data, onSave, onUpdate, onDelete }: DataProps) {
  const [rows, setRows] = React.useState(data);
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});
  const [open, setOpen] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const mode = React.useRef<'view' | 'edit' | 'new'>('view') as React.MutableRefObject<'view' | 'edit' | 'new'>;
  const [levels, setLevels] = React.useState<any[]>([]);
  const [active, setActive] = React.useState<number | null>(null);
  const [loading, setLoading] = React.useState({ id: undefined, loading: false });

  const dispatch = useDispatch<AppDispatch>();
  const {
    Data: DataLevels,
  } = useSelector((state: any) => state.levels);

  function EditToolbar(props: EditToolbarProps) {
    const { setRows, setRowModesModel } = props;

    const handleClick = () => {
      const id = randomId();
      setRows((oldRows) => [
        ...oldRows, {
          id,
          titulo: '',
          nivel_id: undefined,
          imagem: '',
          audio: '',
          ativo: 1,
          created_at: new Date().toLocaleDateString(),
          isNew: true
        }]);
      setRowModesModel((oldModel) => ({
        ...oldModel,
        [id]: { mode: GridRowModes.Edit, fieldToFocus: 'titulo' },
      }));

      mode.current = 'new';
    };

    return (
      <GridToolbarContainer>
        <Button
          color="primary"
          variant="contained"
          startIcon={<AddIcon />}
          disabled={loading.loading || mode.current !== 'view'}
          onClick={handleClick}>
          Adicionar
        </Button>
      </GridToolbarContainer>
    );
  }

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
    mode.current = 'view';
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleValidationData = (data: any) => {
    const { titulo, nivel_id, imagem, ativo } = data;
    if (!titulo) {
      return { isValid: false, message: 'Título é obrigatório.' };
    }
    if (!nivel_id) {
      return { isValid: false, message: 'Nível é obrigatório.' };
    }
    if (!imagem) {
      return { isValid: false, message: 'Imagem é obrigatória.' };
    }
    if (!data?.audio) {
      return { isValid: false, message: 'Áudio é obrigatório.' };
    }

    if (ativo === null) {
      return { isValid: false, message: 'Ativo é obrigatório.' };
    }

    return { isValid: true };
  }

  const handleEditClick = (id: GridRowId) => () => {
    if (mode.current === 'edit') {
      setMessage('Não é possível editar mais de um registro por vez.');
      setOpen(true);
      return;
    }
    mode.current = 'edit';
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id: GridRowId) => () => {
    Swal.fire({
      title: 'Atenção',
      text: 'Deseja mesmo apagar este FlashCard?',
      icon: 'warning',
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sim, deletar!',
      cancelButtonText: 'Cancelar',
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading({ id, loading: true });
        const response = await onDelete(id);
        if (response === true) {
          successToast('FlashCard deletado com sucesso!');
          setRows(rows.filter((row) => row.id !== id));
          setLoading({ id, loading: false });
        } else {
          setOpen(true);
          setMessage('Erro ao deletar FlashCard.');
          setLoading({ id, loading: false });
        }
      }
    });
  };

  const handleCancelClick = (id: GridRowId) => () => {
    mode.current = 'view';
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row.id === id);
    if (editedRow!.isNew) {
      setRows(rows.filter((row) => row.id !== id));
    }
  };

  const handleLevels = async () => {
    await dispatch(LevelsOperations.getLevels());
  }

  React.useEffect(() => {
    if (DataLevels && DataLevels.length > 0) {
      setLevels(DataLevels);
    }
  }, [DataLevels]);

  const processRowUpdate = async (newRow: GridRowModel) => {
    const updatedRow = {
      ...newRow,
      isNew: false,
      imagem: typeof newRow.imagem === 'object' ? newRow.imagem : '',
      audio: typeof newRow.audio === 'object' ? newRow.audio : '',
      id: newRow.id,
      ativo: active,
    }

    if (mode.current === 'new') {
      const validation = handleValidationData(updatedRow);

      if (!validation.isValid) {
        setOpen(true);
        setMessage(validation.message);
        return;
      }

      setLoading({ id: newRow.id, loading: true });
      const response = await onSave(updatedRow);

      if (response.status === 'success') {
        mode.current = 'view';
        successToast(response.message);
        const returnedRow = {
          ...updatedRow,
          imagem: response.data?.imagem,
          audio: response.data?.audio,
          nivel: response.data?.nivel,
          criado_em: response.data?.criado_em,
        }
        setRows(rows.map((row) => (row.id === newRow.id ? returnedRow : row)));
        setLoading({ id: newRow.id, loading: false });
        return returnedRow;

      } else {
        setOpen(true);
        setMessage(response.message);
        setLoading({ id: newRow.id, loading: false });
        return;
      }
    } else { // Edit mode
      setLoading({ id: newRow.id, loading: true });

      const response = await onUpdate(updatedRow) as unknown as ResponseProps;

      if (response.status === 'success') {
        mode.current = 'view';
        successToast(response.message);
        
        const returnedRow = {
          ...updatedRow,
          imagem: response.data?.imagem,
          audio: response.data?.audio,
          nivel: response.data?.nivel,
          nivel_id: newRow.nivel_id,
          ativo: Number(response.data?.ativo),
        }
        setRows(rows.map((row) => (row.id === newRow.id ? returnedRow : row)));
        setLoading({ id: newRow.id, loading: false });
        return returnedRow;
      } else {
        setOpen(true);
        setMessage(response.message);
        setLoading({ id: newRow.id, loading: false });
      }
    }
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const handleVerifyEditMode = (params: any) => {
    if (mode.current === 'edit') {
      setMessage('Não é possível editar mais de um registro por vez.');
      setOpen(true);

      handleCancelClick(params.id)();
      mode.current = 'edit';
      return;
    } else {
      mode.current = 'edit';
    }
  }

  React.useEffect(() => {
    setRows(data);
  }, [data]);

  React.useEffect(() => {
    handleLevels();
    // eslint-disable-next-line
  }, []);

  const columns: GridColDef[] = [
    {
      field: 'id',
      editable: false,
      type: 'number',
    },
    {
      field: 'titulo',
      minWidth: 80,
      flex: 1,
      editable: true,
      type: 'string',
      headerAlign: 'left',
      renderHeader: () => <strong>Título</strong>,
      renderCell: (params) => (
        <strong>{params.value as string}</strong>
      ),
    },
    {
      field: 'nivel_id',
      minWidth: 80,
      flex: 1,
      editable: true,
      type: 'string',
      headerAlign: 'left',
      renderHeader: () => <strong>Curso</strong>,
      renderCell: (params) => {
        return levels.find((item: LevelProps) => item.id === Number(params.value[0]))?.nivel;
      },
      renderEditCell: (params) => {
        const fieldParams = {
          ...params,
          height: 76,
          items: levels,
          column: 'nivel_id',
          nome: 'nivel',
        }

        return <MultipleSelect params={fieldParams} />
      },
    },
    {
      field: 'imagem',
      type: 'string',
      minWidth: 80,
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      editable: true,
      renderHeader: () => <strong>Imagem</strong>,
      renderCell: (params) => {
        return <img
          src={params.value as string}
          alt={params.value as string}
          style={{ height: 76, padding: 2, cursor: 'pointer' }}
          onClick={() => showImage(params.value as string)}
        />
      },
      renderEditCell: (params) => {
        const data = {
          ...params,
          height: 76,
        }
        return <ImageUploader params={data} />
      }
    },
    {
      field: 'audio',
      type: 'string',
      minWidth: 320,
      flex: 1,
      editable: true,
      headerAlign: 'center',
      align: 'center',
      renderHeader: () => <strong>Áudio</strong>,
      renderCell: (params) => ShowAudio(params.value as string),
      renderEditCell: (params) => <AudioUploader params={params} />,
    },
    {
      field: 'ativo',
      type: 'string',
      minWidth: 70,
      flex: 1,
      editable: true,
      headerAlign: 'center',
      align: 'center',
      renderHeader: () => <strong>Ativo</strong>,
      renderCell: (params) => (
        params.value ? 'Sim' : 'Não'
      ),
      renderEditCell: (params) => {
        const data = {
          ...params,
          height: 76,
        }
        return <Choice params={data} setActive={setActive} />
      },
    },
    {
      field: 'criado_em',
      headerAlign: 'center',
      type: 'string',
      minWidth: mode.current === 'view' ? 180 : 0,
      flex: 1,
      editable: false,
      renderHeader: () => <strong>Criado em</strong>,
    },
    {
      field: 'actions',
      type: 'actions',
      renderHeader: () => <strong>Ações</strong>,
      cellClassName: 'actions',
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              sx={{
                color: 'primary.main',
              }}
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={(loading.id === id && loading.loading) ? <CircularProgress size={16} /> : <EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            disabled={loading.loading}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={handleDeleteClick(id)}
            disabled={loading.loading}
            color="inherit"
          />,
        ];
      },
    },
  ];

  return (
    <Box
      sx={{
        width: '100%',
        '& .actions': {
          color: 'text.secondary',
        },
        '& .textPrimary': {
          color: 'text.primary',
        },
      }}
    >
      <Toast
        message={message}
        open={open}
        setclose={setOpen}
        severity="error"
      />

      <DataGrid
        rows={rows}
        columns={columns}
        getRowHeight={() => 80}
        editMode="row"
        disableColumnMenu
        onRowDoubleClick={handleVerifyEditMode}
        columnVisibilityModel={{
          criado_em: mode.current === 'view',
          id: false,
        }}
        autosizeOptions={{
          columns: ['titulo', 'nivel_id', 'imagem', 'audio', 'ativo', 'created_at'],
          includeOutliers: true,
          includeHeaders: true,
        }}
        localeText={{
          noRowsLabel: 'Nenhum FlashCard encontrado.',
          footerRowSelected: (count) => `${count} flashcard(s) selecionado(s)`,
        }}
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        slots={{
          toolbar: EditToolbar as unknown as GridSlots['toolbar'],
        }}
        slotProps={{
          toolbar: { setRows, setRowModesModel } as CustomGridToolbarProps,
          pagination: {
            labelRowsPerPage: "FlashCards por página",
            labelDisplayedRows: ({ from, to, count }) => `${from}-${to} de ${count}`,
          }
        }}
      />
    </Box>
  );
}